import React from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";
import { SVGRoomNames } from "modules/customFloorPlan/constants";
import { IRoom } from "types";
import { useRenameRoom } from "modules/room/hooks";
import { selectCurrentEventId } from "modules/event/selectors";
import { selectTheaterSpaces } from "modules/theater/selectors";
import { selectIsMobile } from "modules/deviceInfo";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Edit } from "@remo-co/ui-core/src/icons/Edit";
import { useStyles } from "./styles";

export type DisplayPosition = "bottom-right" | "right";

interface Props {
  room: IRoom;
  displayPosition: DisplayPosition;
  className?: string;
}

const RoomNameEditButton = React.memo<Props>(
  ({ room, displayPosition, className }) => {
    const currentEventId = useSelector(selectCurrentEventId);
    const spaces = useSelector(selectTheaterSpaces);
    const styles = useStyles();
    const isMobileView = useSelector(selectIsMobile);
    const { showDialog } = useRenameRoom();

    const handleClick = () => {
      showDialog({
        currentEventId,
        hasMultipleFloors: spaces.length > 1,
        currentRoomName: room.name,
        roomId: room.id,
        roomCode: room.code,
      });
    };

    if (
      // Don't show edit icon to mobile/tablet users, because it cannot be clicked
      isMobileView ||
      // Don't show edit icon if this room is the Stage, as we don't display the stage name
      room.code === SVGRoomNames.STAGE
    ) {
      return null;
    }

    return (
      <IconButton
        disableRipple
        data-testid="room-name-edit-icon"
        onClick={handleClick}
        size="medium"
        className={classNames(className, styles.root, {
          [styles.bottomRight]: displayPosition === "bottom-right",
          [styles.right]: displayPosition === "right",
        })}
      >
        <Edit />
      </IconButton>
    );
  },
);

export default RoomNameEditButton;
